<template>
  <VForm v-scroll-to-first-error @submit.prevent="save">
    <VRow no-gutters wrap justify="space-between" class="flex-md-row-reverse">
      <VCol cols="12" md="6" lg="4" class="relative">
        <CompanyStatusPanel
          v-if="company.profileCompleteness"
          :company="company"
          class="mb-7 company-status"
        />
      </VCol>
      <VCol cols="12" md="6" lg="5" xl="3">
        <VTextField
          id="organization"
          v-model="name"
          outlined
          dense
          type="text"
          autocomplete="organization"
          :error-messages="errors.name"
        >
          <template v-slot:label>
            {{ $t('name') }} *
          </template>
        </VTextField>
      </VCol>
    </VRow>
    <VRow no-gutters>
      <VCol cols="12" md="6" lg="5" xl="3">
        <VTextField
          id="site"
          v-model="site"
          class="mb-md-10 mb-lg-0"
          outlined
          dense
          type="url"
          autocomplete="url"
          :label="$t('site')"
          :error-messages="errors.site"
        />
      </VCol>
    </VRow>
    <VRow no-gutters>
      <VCol cols="12" lg="8" xl="6" class="mb-6">
        <Editor
          v-model="about"
          :label="$t('about')"
          required
          :error-messages="errors.about"
          @input="errors.about = []"
        />
      </VCol>
    </VRow>

    <VRow no-gutters>
      <VCol cols="12" lg="8" xl="6">
        <div class="font-weight-medium mb-1">
          {{ $t('address') }}
        </div>
        <AddressInput
          v-for="(address, i) in company.addresses"
          :key="address.id"
          v-model="company.addresses[i]"
          class="mt-3"
        />
        <AddressInput
          v-if="newAddress"
          v-model="newAddress"
          class="mt-3 mb-9"
          @created="attachAddress"
          @cancel="newAddress = null"
        />
        <div v-else class="mb-9">
          <span class="primary--text pseudo-link body-2" @click="addAddress">
            {{ $t('addAddress') }}
          </span>
        </div>
      </VCol>
    </VRow>

    <VRow no-gutters>
      <VCol cols="12" class="mb-9">
        <div class="font-weight-medium mb-2">
          {{ $t('logo') }}
        </div>
        <VCard
          v-if="!hasLogo"
          height="136px"
          width="212px"
          outlined
          xs5
          class="file-upload-card"
          @click="showLogoDialog"
        >
          <div class="primary--text">
            <VIcon color="primary" small>
              mdi-plus
            </VIcon>
            <span class="pseudo-link body-2">
              {{ $t('uploadLogo') }}
            </span>
          </div>
          <span>{{ $t('pictureTypeTip') }}</span>
        </VCard>

        <VCard
          v-if="hasLogo"
          width="140"
          outlined
          xs5
          tile
          class="file-upload-card mb-8"
        >
          <VImg :src="logo" :alt="$t('logo')" />
          <div
            class="primary--text file-upload-card__other pseudo-link body-2"
            @click="destroyLogo"
          >
            {{ $t('delete') }}
          </div>
        </VCard>
        <VDialog
          v-model="logoDialog"
          width="500"
          :scrollable="false"
        >
          <VCard class="pt-5 pb-5 pl-10">
            <VImageInput
              v-model="uploadedLogo"
              class="mx-auto text-center huntica-image-input"
              :image-quality="90"
              min-scaling="contain"
              clearable
              image-format="png"
              overlay-padding="0"
              :full-height="true"
              :full-width="true"
              :clear-icon="clearIcon"
              :upload-icon="uploadIcon"
              :rotate-clockwise-icon="flipVerticallyIcon"
              :rotate-counterclockwise-icon="flipVerticallyIcon"
              :flip-horizontally-icon="flipHorizontallyIcon"
              :flip-vertically-icon="flipHorizontallyIcon"
            />
            <div class="d-flex justify-center pr-10">
              <VBtn color="error" class="mx-1" @click="closeLogoDialog">
                {{ $t('close') }}
              </VBtn>
              <VBtn :loading="uploadingLogo" color="primary" class="mx-1" @click="storeLogo">
                {{ $t('upload') }}
              </VBtn>
            </div>
          </VCard>
        </VDialog>
      </VCol>
    </VRow>

    <div class="font-weight-medium mb-2">
      {{ $t('officePictures') }}
    </div>
    <VRow no-gutters>
      <VCol cols="12" lg="10" class="flex-wrap d-flex">
        <div v-for="photo of company.officePhotos" :key="photo.id" class="office-photo">
          <VImg
            :src="photo.preview.url"
            height="100%"
            class="office-photo__img"
          />
          <div class="office-photo__remove white--text" @click="destroyOfficePhoto(photo)">
            {{ $t('delete') }}
          </div>
        </div>
        <VCard
          v-if="photosUploadAvailable"
          elevation="0"
          class="office-photo-upload file-upload-card"
          tile
          color="grey lighten-3"
        >
          <div v-if="!photoUploading" class="primary--text">
            <VIcon color="primary" small>
              mdi-plus
            </VIcon>
            <label class="pseudo-link" for="select-office-photos">{{ $t('upload') }}</label>
            <VFileInput
              id="select-office-photos"
              v-model="uploadedOfficePhotos"
              accept="image/jpeg, image/jpg, image/png"
              multiple
              style="display: none"
              @change="storeOfficePhotos"
            />
          </div>
          <div v-else class="primary--text">
            <Preloader />
          </div>
          <span>{{ $t('pictureTypeTip2') }}</span>
        </VCard>
      </VCol>
    </VRow>
    <VRow no-gutters>
      <VCol v-if="hasErrorMessageOnly" class="mt-2 red--text" col="12">
        {{ errorMessage }}
      </VCol>
    </VRow>
    <VRow no-gutters>
      <VCol>
        <VBtn
          color="success"
          dark
          elevation="0"
          class="mt-2"
          type="submit"
          :loading="loading"
        >
          {{ $t('save') }}
        </VBtn>
      </VCol>
    </VRow>
  </VForm>
</template>

<script>
import {
  mdiClose,
  mdiFormatRotate90,
  mdiRotateLeftVariant,
  mdiCloudUpload,
} from '@mdi/js';
import VImageInput from 'vuetify-image-input';
import ValidationMixin from '../../mixins/ValidationMixin';
import { dataURIToFile, objectToFormData, safeGet } from '../../functions';
import AddressInput from '../Address/AddressInput.vue';
import Editor from '../Input/Editor.vue';
import CompanyStatusPanel from './StatusPanel.vue';
import Preloader from '../Preloader.vue';
import ScrollToFirstError from '../../directives/ScrollToFirstError';
import config from '../../config';
import Company from '../../orm/models/Company/Company';
import Logo from '../../orm/models/Company/Logo';
import OfficePhoto from '../../orm/models/Company/OfficePhoto';
import Address from '../../orm/models/Address/Address';

export default {
  name: 'CompanyForm',
  components: {
    Preloader,
    CompanyStatusPanel,
    Editor,
    AddressInput,
    VImageInput,
  },
  directives: { ScrollToFirstError },
  mixins: [ValidationMixin],
  props: {
    company: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      name: this.company.name,
      site: this.company.site,
      about: this.company.about,
      clearIcon: mdiClose,
      flipVerticallyIcon: mdiFormatRotate90,
      flipHorizontallyIcon: mdiRotateLeftVariant,
      uploadIcon: mdiCloudUpload,
      logoDialog: false,
      uploadedLogo: '',
      uploadingLogo: false,
      uploadedOfficePhotos: [],
      photoUploading: false,
      destroyLogoLoading: false,
      newAddress: null,
    };
  },
  computed: {
    siteUrl() {
      if (!this.site) {
        return null;
      }

      return this.site.startsWith('http://') || this.site.startsWith('https://')
        ? this.site
        : `http://${this.site}`;
    },
    logo() {
      return safeGet(this.company, ['logo', 'file', 'url']);
    },
    hasLogo() {
      return this.logo !== null;
    },
    photosUploadAvailable() {
      return this.company.officePhotos.length < config.officePhotos.max;
    },
  },
  async created() {
    this.fetchStatus();
  },
  methods: {
    async fetchStatus() {
      await Company.api().fetchProfileCompleteness(this.company.id);
    },
    async save() {
      await this.withValidationHandling(async () => {
        await Company.api().update(this.company.id, {
          name: this.name,
          site: this.siteUrl,
          about: this.about,
        });
        this.$toast(this.$t('dataSave'));
        this.fetchStatus();
      });
    },
    showLogoDialog() {
      this.logoDialog = true;
    },
    closeLogoDialog() {
      this.logoDialog = false;
    },
    async storeLogo() {
      this.withLoading(async () => {
        await Logo.api().store(this.company.id, objectToFormData({
          logo: dataURIToFile(this.uploadedLogo, 'logo.png'),
        }));
        this.closeLogoDialog();
        await this.fetchStatus();
      }, 'uploadingLogo');
    },
    destroyLogo() {
      this.withLoading(async () => {
        await Logo.api().destroy(this.company.logo.id);
        await this.fetchStatus();
      }, 'destroyLogoLoading');
    },
    async storeOfficePhotos(photos) {
      try {
        await this.withLoading(async () => {
          await OfficePhoto.api().store(this.company.id, objectToFormData({ photos }));
        }, 'photoUploading');
      } catch (e) {
        this.$toast.error(e.message);
      }
      await this.fetchStatus();
    },
    async destroyOfficePhoto(photo) {
      await OfficePhoto.api().destroy(photo.id);
      await this.fetchStatus();
    },
    async addAddress() {
      this.newAddress = await Address.new();
    },
    attachAddress(address) {
      this.withApiErrorHandling(async () => {
        await Company.api().update(this.company.id, {
          addresses: [...this.company.addresses.map((a) => a.id), address.id],
        });
        this.newAddress = null;
      }, (e) => this.$toast.error(e.message));
    },
  },
};
</script>

<style lang="scss">
  .huntica-image-input img {
    left: 0;
  }
</style>

<style scoped lang="scss">
  .company-status {
    max-width: 290px;
    width: 100%;
  }
  .file-upload-card {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    flex-direction: column;
    position: relative;
    &__other {
      position: absolute;
      top: calc(100% + #{$spacer * 3});
    }
  }
  .office-photo-upload {
    width: 265px;
    height: 180px;
    margin-bottom: $spacer * 5;
  }
  .office-photo {
    max-width: 265px;
    height: 180px;
    position: relative;
    margin-bottom: $spacer * 5;
    &.file-upload-card {
      &:before {
        display: none;
      }
    }
    &__img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    &:before {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 30%;
      background: linear-gradient(180deg, rgba(51, 51, 51, 0.7) 0%, rgba(51, 51, 51, 0) 100%);
      transform: rotate(-180deg);
      z-index: 1;
    }
    &__remove {
      position: absolute;
      cursor: pointer;
      bottom: $spacer * 5;
      left: 50%;
      transform: translateX(-50%);
      z-index: 2;
    }
    &:before, &__remove {
      transition: 0.3s;
      opacity: 0;
    }
    &:hover {
      &:before, .office-photo__remove {
        opacity: 1;
      }
    }
  }

  @media #{map-get($display-breakpoints, 'sm-and-up')} {
    .office-photo {
      margin-right: $spacer * 5;
    }
  }

  @media #{map-get($display-breakpoints, 'md-and-up')} {
    .company-status {
      position: absolute;
      right: 0;
      top: 0;
    }
  }
</style>

<i18n locale="en" lang="yaml">
  name: Company name
  site: Website
  about: About
  address: Address
  addAddress: New address
  logo: Logo
  uploadLogo: Upload logo
  pictureTypeTip: jpg, png no more than 1MB
  delete: Delete
  close: Close
  upload: Upload
  save: Save
  dataSave: Data saved
  pictureTypeTip2: jpg, png no larger than 5MB
  officePictures: Office pictures
</i18n>

<i18n locale="ru" lang="yaml">
  name: Название
  site: Сайт компании
  about: Описание
  address: Адрес
  addAddress: Добавить адрес
  logo: Логотип
  uploadLogo: Загрузить логотип
  pictureTypeTip: jpg, png не более 1 мб
  delete: Удалить
  close: Закрыть
  upload: Загрузить
  save: Сохранить
  dataSave: Данные изменены
  pictureTypeTip2: jpg, png не более 5 мб
  officePictures: Фотографии офиса
</i18n>
